import { userManagementLocators } from '@locators/userManagement';
import {
  Button,
  FilterButton,
  Input,
  Link,
  OptionList,
  Popover,
  Stack,
} from '@servicetitan/design-system';
import { useEffect, useState } from 'react';

interface Option<T> {
  name: string;
  value: T;
  icon?: JSX.Element;
}

export interface MultiSelectFilterProps<T> {
  id?: string;
  name: string;
  options: Option<T>[];
  optionComponent?: React.ComponentType<Option<T>>;
  onChange(value: T[]): void;
  value: T[];
  customSearchFilter?: (search: string) => string;
  scrollHeight?: string;
  disabled?: boolean;
  className?: string;
  enableValueSync?: boolean;
}

export const MultiSelectFilter = <T,>({
  id,
  onChange,
  value,
  name,
  options,
  optionComponent: OptionComponent,
  customSearchFilter = (search) => search,
  scrollHeight = '50vh',
  disabled,
  className,
  enableValueSync = true,
}: MultiSelectFilterProps<T>) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tempValue, setTempValue] = useState<T[]>(value);

  useEffect(() => {
    if (enableValueSync) {
      setTempValue(value);
    }
  }, [enableValueSync, value]);

  const optionListOptions = [
    {
      text: 'Selected',
      options: options
        .filter((option) => tempValue.includes(option.value))
        .filter((option) =>
          customSearchFilter(option.name)
            .toLowerCase()
            .includes(customSearchFilter(search).toLowerCase()),
        )
        .map((option) => ({
          ...option,
          value: option.value,
          content: OptionComponent ? (
            <OptionComponent {...option} />
          ) : (
            option.name
          ),
        })),
    },
    {
      text: 'All Others',
      options: options
        .filter((option) => !tempValue.includes(option.value))
        .filter((option) =>
          customSearchFilter(option.name)
            .toLowerCase()
            .includes(customSearchFilter(search).toLowerCase()),
        )
        .map((option) => ({
          ...option,
          value: option.value,
          content: OptionComponent ? (
            <OptionComponent {...option} />
          ) : (
            option.name
          ),
        })),
    },
  ];

  return (
    <Popover
      header={
        <Input
          className='m-r-1 m-l-2 w-100'
          size='small'
          icon='search'
          iconPosition='left'
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          disabled={disabled}
        />
      }
      footer={
        <Stack justifyContent='flex-start' className='w-100 m-x-1'>
          <Stack.Item>
            <Link color='primary' className='m-l-1 fs-2' disabled>
              Select
            </Link>
          </Stack.Item>
          <Stack.Item>
            <Link
              primary
              className='m-l-1 fs-2'
              onClick={() =>
                setTempValue(options.map((option) => option.value))
              }
              aria-label='select all'
            >
              All
            </Link>
            <Link
              primary
              className='m-l-1 fs-2'
              onClick={() => setTempValue([])}
              aria-label='select none'
            >
              None
            </Link>
          </Stack.Item>
          <Stack.Item className='m-l-auto'>
            <Button
              id={
                userManagementLocators.bulkActions.didAccountFilterApplyButton
              }
              xsmall
              primary
              onClick={() => {
                setOpen(false);
                onChange(tempValue);
              }}
            >
              Apply
            </Button>
          </Stack.Item>
        </Stack>
      }
      el='span'
      direction='br'
      padding='s'
      onClickOutside={() => {
        setOpen(false);
        setTempValue(value);
      }}
      open={open}
      portal
      scrollHeight={scrollHeight}
      autoFlipVertically
      trigger={
        <FilterButton
          id={id}
          onClick={() => setOpen(true)}
          value={value.length || undefined}
          label={name}
          expandIcon={open}
          disabled={disabled}
        />
      }
      className={className}
    >
      <OptionList
        data-cy={userManagementLocators.bulkActions.didAccountFilterOptionsList}
        flatGroup={true}
        options={
          optionListOptions.every(
            (optionListOption) => optionListOption.options.length,
          )
            ? optionListOptions
            : optionListOptions.find(
                (optionListOption) => optionListOption.options.length,
              )?.options || []
        }
        multiple
        onChange={(data: T, checked: boolean) => {
          if (checked) {
            setTempValue((prevState) => [...prevState, data]);
          } else {
            setTempValue((prevState) =>
              [...prevState].filter((item) => item !== data),
            );
          }
        }}
        value={tempValue}
      />
    </Popover>
  );
};
